import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { useMemo } from 'react';

import {
	type IInventoryItem,
	InventoryItemCondition,
	Services,
	type ServicesProviderInventory,
	validateReadyForOnlineSale,
} from '@ekhodealer/ekho-common';

import type { UseQueryResult } from '@tanstack/react-query';
import type { CategorizedOnlineSalesItems } from '../../hooks/useEnableOnlineSalesSelection';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useGetInventoryItems } from '../../api/useGetInventoryItems';

import { ActionConfirmationModal, type CategoryRow } from './ActionConfirmationModal';

interface EnableOnlineSalesModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	selectedIds: Set<string>;
	totalSelected: number;
	onConfirm: (readyItems: IInventoryItem[]) => void;
	onCancel: () => void;
	/** Number of items enablement was initiated for (0 means not yet confirmed) */
	enableInitiatedCount: number;
	/** Whether the enable operation is currently pending */
	isEnabling?: boolean;
}

/**
 * Get validation failure reasons for an inventory item
 */
function getValidationFailureReasons(
	item: IInventoryItem,
	servicesProvider: ServicesProviderInventory,
): string[] {
	const reasons: string[] = [];
	const onlineSaleConfig = {
		legalAddress: servicesProvider?.legalAddress,
		config: servicesProvider?.config,
	};

	if (item.newUsed !== InventoryItemCondition.NEW) {
		reasons.push('Not a new vehicle');
	}

	if (item.matching?.status !== 'MATCHED') {
		reasons.push('Vehicle not fully matched');
	}

	if (!item.pricing?.retailPrice || item.pricing.retailPrice <= 0) {
		reasons.push('Missing or invalid retail price');
	}

	if (!item.vin || item.vin.length === 0) {
		reasons.push('Missing VIN');
	}

	if (!onlineSaleConfig?.config?.[Services.INVENTORY]?.isLiveForOnlineSales) {
		reasons.push('Dealership not configured for online sales');
	}

	return reasons;
}

/**
 * Categorize items into ready, notEligible, and alreadyEnabled groups
 */
const categorizeOnlineSalesItems = (
	items: UseQueryResult<IInventoryItem, Error>[],
	servicesProvider: ServicesProviderInventory,
): CategorizedOnlineSalesItems => {
	const result: CategorizedOnlineSalesItems = {
		ready: [],
		notEligible: [],
		alreadyEnabled: [],
	};

	if (!servicesProvider) {
		return result;
	}


	for (const queryResult of items) {
		if (queryResult.error || !queryResult.data) {
			continue;
		}

		const item = queryResult.data;

		// Check if already enabled
		if (item.isMarkedForOnlineSale) {
			result.alreadyEnabled.push(item);
			continue;
		}

		// Validate if ready for online sales
		const isValid = validateReadyForOnlineSale(item, servicesProvider as ServicesProviderInventory);

		if (isValid) {
			result.ready.push(item);
		} else {
			const reasons = getValidationFailureReasons(item, servicesProvider);
			result.notEligible.push({ item, reasons });
		}
	}

	return result;
};

/**
 * Modal for confirming enable online sales operation on selected inventory items
 * Fetches items using query hooks and categorizes them
 * Shows categorized items: ready to enable, not eligible, and already enabled
 * After confirmation, shows immediate success message
 */
export const EnableOnlineSalesModal = ({
	isOpen,
	onOpenChange,
	selectedIds,
	totalSelected,
	onConfirm,
	onCancel,
	enableInitiatedCount,
	isEnabling = false,
}: EnableOnlineSalesModalProps) => {
	const { data: servicesProvider } = useCurrentServicesProvider() as UseQueryResult<
		ServicesProviderInventory,
		Error
	>;

	// Fetch all selected items using query hooks
	const itemQueries = useGetInventoryItems(selectedIds, isOpen && selectedIds.size > 0);

	// Categorize items after fetching
	const categorizedItems = useMemo(() => {
		if (!isOpen || selectedIds.size === 0 || !servicesProvider) {
			return { ready: [], notEligible: [], alreadyEnabled: [] };
		}
		return categorizeOnlineSalesItems(itemQueries, servicesProvider);
	}, [isOpen, selectedIds.size, itemQueries, servicesProvider]);

	const { ready, notEligible, alreadyEnabled } = categorizedItems;

	// Check if we're still loading
	const isLoading = itemQueries.some((query) => query.isPending);

	// Build category rows for the generic modal
	const categories: CategoryRow[] = [
		{
			icon: <CheckCircleIcon />,
			iconColor: 'var(--color-positive, #22c55e)',
			count: ready.length,
			message:
				ready.length === 1 ? 'is ready for online sales' : 'are ready for online sales',
		},
		{
			icon: <ErrorIcon />,
			iconColor: 'var(--color-negative, #ef4444)',
			count: notEligible.length,
			message:
				notEligible.length === 1
					? 'cannot be enabled for online sales'
					: 'cannot be enabled for online sales',
		},
		{
			icon: <InfoIcon />,
			iconColor: 'var(--color-secondary, #6b7280)',
			count: alreadyEnabled.length,
			message:
				alreadyEnabled.length === 1
					? 'is already enabled for online sales'
					: 'are already enabled for online sales',
		},
	];

	const handleConfirm = () => {
		onConfirm(ready);
	};

	return (
		<ActionConfirmationModal
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			categories={categories}
			totalSelected={totalSelected}
			readyCount={ready.length}
			onConfirm={handleConfirm}
			onCancel={onCancel}
			actionInitiatedCount={enableInitiatedCount}
			isConfirming={isEnabling || isLoading}
			title='Review before enabling'
			subtitle='You selected {count} inventory item{plural}'
			successTitle='Online sales enabled'
			successIcon={<StorefrontIcon />}
			successMessage='item{plural} enabled for online sales'
			successDescription='The selected items have been marked for online sale and will be published to your storefront.'
			confirmButtonText='Enable {count} now'
			noItemsMessage='No items can be enabled for online sales. Ensure items are new, fully matched, have retail prices, and have VINs.'
		/>
	);
};
